import * as React from 'react';
import { DayItem, EmployeeRowData, ActualHoursEmployeeRowData, RolesByType } from '../types';
import { NotificationVariant } from '../../../components/DLONotification';
import { transformEmployeeRowsToAPIFormat, transformActualHoursToAPIFormat, compareWeek, getOrphanedScheduleEntries, getEmployeeOptions, getShiftOptions } from '../utils/scheduleHelpers';
import { validateEmployeeHoursOver40, validateUnassignedRoles, validateForecastVariance, getEmployeesOver40Hours, getUnassignedRoleDetails, getForecastVarianceDetails } from '../utils/validationHelpers';
import { hasChildSplitRoles } from '../utils/handlerHelpers';
import { fetchCreateScheduleHours, fetchScheduleEmployeeData } from '../../../../../store/thunks/dloThunks'; // Add missing import for fetchCreateScheduleHours thunk
import { fetchKCPHData } from '../../../../../store/thunks/dloThunks';
import { fromBackendWeek } from '../../../utils/weekTransforms';
import { getSessionLockService } from '../../../../../api/sessionLockService';

interface UseCreateScheduleHandlersProps {
  createScheduleData: any;
  employeeRows: EmployeeRowData[];
  deletedEmployeeRows: EmployeeRowData[];
  actualEmployeeRows: ActualHoursEmployeeRowData[];
  actualDeletedEmployeeRows: ActualHoursEmployeeRowData[];
  selectedDays: string[];
  actualSelectedDays: string[];
  tableData: any[];
  tabState: { activeTab: 'schedule' | 'actual' };
  showNotification: (message: string, variant?: NotificationVariant) => void;
  setModalState: any;
  createSchedulePeriod: number;
  createScheduleWeek: number;
  createScheduleYear: number;
  appData: any;
  onNavigate?: (page: string, params?: any) => void;
  setActiveTab: (type: 'schedule' | 'actual') => void;
  targetWeek?: { period: number; week: number; year: number };
  currentWeek?: { period: number; week: number; year: number };
  hasUnsavedChanges: boolean;
  scheduleEmployeeData?: any;
  initialSchedules?: any;
  employees?: any;
  shiftTimes?: any;
  onCloseAllDropdowns?: () => void;
}

export const useCreateScheduleHandlers = ({
  createScheduleData,
  employeeRows,
  deletedEmployeeRows,
  actualEmployeeRows,
  actualDeletedEmployeeRows,
  selectedDays,
  actualSelectedDays,
  tableData,
  tabState,
  showNotification,
  setModalState,
  createSchedulePeriod,
  createScheduleWeek,
  createScheduleYear,
  appData,
  onNavigate,
  setActiveTab,
  targetWeek,
  currentWeek,
  hasUnsavedChanges,
  scheduleEmployeeData,
  initialSchedules,
  employees,
  shiftTimes,
  onCloseAllDropdowns
}: UseCreateScheduleHandlersProps) => {
  const {
    dispatch,
    setFilteredRoles,
    setActualFilteredRoles,
    setDayFilter,
    clearSelectedDays,
    setActualDayFilter,
    clearActualSelectedDays,
    addEmployeeRow,
    updateEmployeeRow,
    removeEmployeeRow,
    updateScheduleHours,
    updateActualHours,
    addActualEmployeeRow,
    updateActualEmployeeRow,
    removeActualEmployeeRow,
    splitEmployeeRole,
    splitRoleAfterSibling,
    removeSplitRole,
    splitActualEmployeeRole,
    splitActualRoleAfterSibling,
    removeActualSplitRole,
    toggleSelectedDay,
    toggleActualSelectedDay,
    saveScheduleData,
    submitScheduleStatus
  } = createScheduleData;

  // Use useRef to store the pending navigation action across renders
  const pendingNavigationActionRef = React.useRef<(() => void) | null>(null);

  // Function to handle navigation with unsaved changes check
  const handleNavigationWithUnsavedCheck = (
    hasUnsavedChanges: boolean,
    navigationAction: () => void
  ) => {
    if (hasUnsavedChanges) {
      pendingNavigationActionRef.current = navigationAction;
      setModalState.setUnsavedChangesModal(
        true,
        'You have unsaved data, are you sure you want to proceed without saving?'
      );
    } else {
      navigationAction();
    }
  };

  // Function to proceed with navigation after user confirms
  const proceedWithUnsavedChanges = () => {
    if (pendingNavigationActionRef.current) {
      pendingNavigationActionRef.current();
      pendingNavigationActionRef.current = null;
    }
  };

  // Navigation handlers
  const handleCreateSchedule = () => {
    console.log('Create Schedule pressed from Create Schedule page');
    // Add create schedule logic here
  };

  const handleLocationSettings = () => {
    console.log('Location Settings pressed from Create Schedule page');
    handleNavigationWithUnsavedCheck(hasUnsavedChanges, () => {
      onNavigate?.('DLOLocationSetting');
    });
  };

  const handleHomePress = () => {
    console.log('Home pressed from Create Schedule page');
    handleNavigationWithUnsavedCheck(hasUnsavedChanges, async () => {
      // Release all active locks before navigating to dashboard
      const lockService = getSessionLockService();
      if (lockService) {
        const serviceLocks = lockService.getActiveLocks();
        if (serviceLocks.size > 0) {
          console.log('[DLOCreateSchedule] Releasing locks on home press:', Array.from(serviceLocks.keys()));
          for (const [resourceId, lockData] of serviceLocks.entries()) {
            try {
              await lockService.releaseLock(resourceId, lockData.lockToken);
              console.log('[DLOCreateSchedule] Lock released:', resourceId);
            } catch (error) {
              console.error('[DLOCreateSchedule] Error releasing lock:', error);
            }
          }
        }
      }
      onNavigate?.('DLODashboard');
    });
  };

  const handleLaborOptimizationPress = () => {
    console.log('Labor Optimization pressed from Create Schedule page');
    handleNavigationWithUnsavedCheck(hasUnsavedChanges, async () => {
      // Release all active locks before navigating to dashboard
      const lockService = getSessionLockService();
      if (lockService) {
        const serviceLocks = lockService.getActiveLocks();
        if (serviceLocks.size > 0) {
          console.log('[DLOCreateSchedule] Releasing locks on labor optimization press:', Array.from(serviceLocks.keys()));
          for (const [resourceId, lockData] of serviceLocks.entries()) {
            try {
              await lockService.releaseLock(resourceId, lockData.lockToken);
              console.log('[DLOCreateSchedule] Lock released:', resourceId);
            } catch (error) {
              console.error('[DLOCreateSchedule] Error releasing lock:', error);
            }
          }
        }
      }
      onNavigate?.('DLODashboard');
    });
  };

  const handleScheduleSettings = () => {
    console.log('Schedule Settings pressed from Create Schedule page');
    handleNavigationWithUnsavedCheck(hasUnsavedChanges, () => {
      onNavigate?.('DLOScheduleSetting', { scheduleType: 'existing_schedule' });
    });
  };

  // Filter handlers
  const handleRoleFilterSave = (selectedRoles: RolesByType) => {
    console.log('Role filter save (applying to both tabs):', selectedRoles);
    // Apply the same role filtering to both schedule and actual tabs
    dispatch(setFilteredRoles(selectedRoles));
    dispatch(setActualFilteredRoles(selectedRoles));
  };

  const handleActualRoleFilterSave = (selectedRoles: RolesByType) => {
    console.log('Actual role filter save (applying to both tabs):', selectedRoles);
    // Apply the same role filtering to both schedule and actual tabs
    dispatch(setFilteredRoles(selectedRoles));
    dispatch(setActualFilteredRoles(selectedRoles));
  };

  const handleDayFilterSave = (filteredDays: DayItem[]) => {
    if (tabState.activeTab === 'actual') {
      handleActualDayFilterSave(filteredDays);
    } else {
      console.log('Schedule day filter save:', filteredDays);

      dispatch(setDayFilter(filteredDays));

      const disabledDays = filteredDays.filter(day => !day.enabled);

      if (disabledDays.length > 0) {
        employeeRows.forEach((row: EmployeeRowData) => {
          if (row.isSelected) {
            handleEmployeeRowUpdate(row.id, { isSelected: false });
          }
        });

        dispatch(clearSelectedDays());

        const disabledDayNames = disabledDays.map(day => day.name.toLowerCase());

        // employeeRows.forEach((row: EmployeeRowData) => {
        //   const updates: any = {};

        //   disabledDayNames.forEach(dayName => {
        //     switch (dayName) {
        //       case 'sunday':
        //         updates.sunday = '0';
        //         break;
        //       case 'monday':
        //         updates.monday = '0';
        //         break;
        //       case 'tuesday':
        //         updates.tuesday = '0';
        //         break;
        //       case 'wednesday':
        //         updates.wednesday = '0';
        //         break;
        //       case 'thursday':
        //         updates.thursday = '0';
        //         break;
        //       case 'friday':
        //         updates.friday = '0';
        //         break;
        //       case 'saturday':
        //         updates.saturday = '0';
        //         break;
        //     }
        //   });

        //   if (Object.keys(updates).length > 0) {
        //     handleEmployeeRowUpdate(row.id, updates);
        //   }
        // });
      }
    }
  };

  const handleActualDayFilterSave = (filteredDays: DayItem[]) => {
    console.log('Actual day filter save:', filteredDays);

    dispatch(setActualDayFilter(filteredDays));

    const disabledDays = filteredDays.filter(day => !day.enabled);

    if (disabledDays.length > 0) {
      actualEmployeeRows.forEach((row: ActualHoursEmployeeRowData) => {
        if (row.isSelected) {
          handleActualEmployeeRowUpdate(row.id, { isSelected: false });
        }
      });

      dispatch(clearActualSelectedDays());
      // Do NOT reset values for disabled days; just clear selection
    }
  };

  // Employee row handlers
  const handleAddEmployeeRow = () => {
    if (tabState.activeTab === 'actual') {
      console.log('Add Actual Employee Row pressed from DLOCreateSchedule');
      dispatch(addActualEmployeeRow());
    } else {
      console.log('Add Schedule Employee Row pressed from DLOCreateSchedule');
      dispatch(addEmployeeRow());
    }
  };

  const handleEmployeeRowUpdate = (id: string, updates: any) => {
    console.log('Employee row update:', id, updates);
    dispatch(updateEmployeeRow({ id, updates }));

    // Check if any day field is being updated and if the employee has a role assigned
    const updatedRow = employeeRows.find((row: EmployeeRowData) => row.id === id);

    // If employee is being changed on a parent split row, propagate the new
    // employee value to all child rows. Split-role child rows have no Employee
    // dropdown of their own and conceptually inherit the parent's employee.
    if (updates.hasOwnProperty('employee') && updatedRow && !updatedRow.isChildRow) {
      const childRows = employeeRows.filter((row: EmployeeRowData) => row.parentId === id);
      childRows.forEach((child: EmployeeRowData) => {
        dispatch(updateEmployeeRow({ id: child.id, updates: { employee: updates.employee } }));
      });
    }
    if (updatedRow) {
      // Define day fields and their corresponding day numbers (1=Sunday, 2=Monday, etc.)
      const dayFields = [
        { field: 'sunday', dayNumber: 1 },
        { field: 'monday', dayNumber: 2 },
        { field: 'tuesday', dayNumber: 3 },
        { field: 'wednesday', dayNumber: 4 },
        { field: 'thursday', dayNumber: 5 },
        { field: 'friday', dayNumber: 6 },
        { field: 'saturday', dayNumber: 7 }
      ];

      // Check if role is being changed
      if (updates.hasOwnProperty('role') && updates.role) {
        const oldRoleName = updatedRow.role ? (updatedRow.role.label || updatedRow.role.id) : null;
        const newRoleName = updates.role.label || updates.role.id;

        // If role is actually changing (not just setting a role when there was none)
        if (oldRoleName && oldRoleName !== newRoleName) {
          // For each day field, update the schedule hours
          dayFields.forEach(({ field, dayNumber }) => {
            const dayHours = parseFloat((updatedRow as any)[field] as string) || 0;
            
            if (dayHours > 0 && appData?.selectedLocation?.id && createSchedulePeriod && createScheduleWeek) {
              // Remove hours from old role
              const oldRoleCumulativeHours = calculateCumulativeHoursForRoleAndDay(
                employeeRows,
                oldRoleName,
                field,
                id,
                0 // Set to 0 to remove from old role
              );

              dispatch(updateScheduleHours({
                role: oldRoleName,
                dayNumber: dayNumber,
                locationId: appData.selectedLocation.id,
                year: createScheduleYear,
                period: createSchedulePeriod,
                week: createScheduleWeek,
                newScheduledHours: oldRoleCumulativeHours
              }));

              // Add hours to new role
              const newRoleCumulativeHours = calculateCumulativeHoursForRoleAndDay(
                employeeRows.map(row => row.id === id ? { ...row, role: updates.role } : row),
                newRoleName,
                field,
                id,
                dayHours
              );

              dispatch(updateScheduleHours({
                role: newRoleName,
                dayNumber: dayNumber,
                locationId: appData.selectedLocation.id,
                year: createScheduleYear,
                period: createSchedulePeriod,
                week: createScheduleWeek,
                newScheduledHours: newRoleCumulativeHours
              }));
            }
          });
        } else if (!oldRoleName && newRoleName) {
          // Role is being set for the first time, add hours to new role
          dayFields.forEach(({ field, dayNumber }) => {
            const dayHours = parseFloat((updatedRow as any)[field] as string) || 0;
            
            if (dayHours > 0 && appData?.selectedLocation?.id && createSchedulePeriod && createScheduleWeek) {
              const newRoleCumulativeHours = calculateCumulativeHoursForRoleAndDay(
                employeeRows.map(row => row.id === id ? { ...row, role: updates.role } : row),
                newRoleName,
                field,
                id,
                dayHours
              );

              dispatch(updateScheduleHours({
                role: newRoleName,
                dayNumber: dayNumber,
                locationId: appData.selectedLocation.id,
                year: createScheduleYear,
                period: createSchedulePeriod,
                week: createScheduleWeek,
                newScheduledHours: newRoleCumulativeHours
              }));
            }
          });
        }
      }

      // Check if any day field is being updated and if the employee has a role assigned
      if (updatedRow.role || updates.role) {
        const roleName = (updates.role ? updates.role.label || updates.role.id : updatedRow.role?.label || updatedRow.role?.id);
        
        // Check each day field that was updated
        dayFields.forEach(({ field, dayNumber }) => {
          if (updates.hasOwnProperty(field) && !updates.hasOwnProperty('role')) {
            const newHoursValue = parseFloat(updates[field]) || 0;
            
            // Calculate cumulative scheduled hours for this role and day
            const cumulativeHours = calculateCumulativeHoursForRoleAndDay(
              employeeRows,
              roleName,
              field,
              id,
              newHoursValue
            );

            // Update schedule hours data in Redux
            if (appData?.selectedLocation?.id && createSchedulePeriod && createScheduleWeek) {
              dispatch(updateScheduleHours({
                role: roleName,
                dayNumber: dayNumber,
                locationId: appData.selectedLocation.id,
                year: createScheduleYear,
                period: createSchedulePeriod,
                week: createScheduleWeek,
                newScheduledHours: cumulativeHours
              }));
            }
          }
        });
      }
    }

    // Handle child row parent total calculation (existing logic)
    if (updatedRow?.isChildRow && updatedRow.parentId) {
      const parentRow = employeeRows.find((row: EmployeeRowData) => row.id === updatedRow.parentId);
      if (parentRow) {
        const childRowWithUpdates = { ...updatedRow, ...updates };

        const parentHours = [
          parseFloat(parentRow.sunday) || 0,
          parseFloat(parentRow.monday) || 0,
          parseFloat(parentRow.tuesday) || 0,
          parseFloat(parentRow.wednesday) || 0,
          parseFloat(parentRow.thursday) || 0,
          parseFloat(parentRow.friday) || 0,
          parseFloat(parentRow.saturday) || 0,
        ].reduce((sum, hours) => sum + hours, 0);

        const childHours = [
          parseFloat(childRowWithUpdates.sunday) || 0,
          parseFloat(childRowWithUpdates.monday) || 0,
          parseFloat(childRowWithUpdates.tuesday) || 0,
          parseFloat(childRowWithUpdates.wednesday) || 0,
          parseFloat(childRowWithUpdates.thursday) || 0,
          parseFloat(childRowWithUpdates.friday) || 0,
          parseFloat(childRowWithUpdates.saturday) || 0,
        ].reduce((sum, hours) => sum + hours, 0);

        const newParentTotal = parentHours + childHours;

        dispatch(updateEmployeeRow({
          id: parentRow.id,
          updates: { total: newParentTotal.toString() }
        }));
      }
    }
  };

  // Helper function to calculate cumulative hours for a role and day
  const calculateCumulativeHoursForRoleAndDay = (
    allEmployeeRows: EmployeeRowData[],
    roleName: string,
    dayField: string,
    updatedRowId: string,
    newHoursValue: number
  ): number => {
    let cumulativeHours = 0;

    allEmployeeRows.forEach((row) => {
      // Only include rows with the same role
      const rowRoleName = row.role?.label || row.role?.id;
      if (rowRoleName === roleName) {
        if (row.id === updatedRowId) {
          // Use the new value for the row being updated
          cumulativeHours += newHoursValue;
        } else {
          // Use existing value for other rows
          const existingValue = parseFloat((row as any)[dayField]) || 0;
          cumulativeHours += existingValue;
        }
      }
    });

    return cumulativeHours;
  };

  const handleActualEmployeeRowUpdate = (id: string, updates: any) => {
    console.log('Actual employee row update:', id, updates);
    dispatch(updateActualEmployeeRow({ id, updates }));

    // Find the updated row
    const updatedRow = actualEmployeeRows.find((row: ActualHoursEmployeeRowData) => row.id === id);

    // If employee is being changed on a parent split row, propagate the new
    // employee value to all child rows. Split-role child rows have no Employee
    // dropdown of their own and conceptually inherit the parent's employee.
    if (updates.hasOwnProperty('employee') && updatedRow && !updatedRow.isChildRow) {
      const childRows = actualEmployeeRows.filter((row: ActualHoursEmployeeRowData) => row.parentId === id);
      childRows.forEach((child: ActualHoursEmployeeRowData) => {
        dispatch(updateActualEmployeeRow({ id: child.id, updates: { employee: updates.employee } }));
      });
    }
    if (updatedRow) {
      // Define day fields and their corresponding day numbers (1=Sunday, 2=Monday, etc.)
      const dayFields = [
        { field: 'sunday', dayNumber: 1 },
        { field: 'monday', dayNumber: 2 },
        { field: 'tuesday', dayNumber: 3 },
        { field: 'wednesday', dayNumber: 4 },
        { field: 'thursday', dayNumber: 5 },
        { field: 'friday', dayNumber: 6 },
        { field: 'saturday', dayNumber: 7 }
      ];

      // Check if role is being changed
      if (updates.hasOwnProperty('role') && updates.role) {
        const oldRoleName = updatedRow.role ? (updatedRow.role.label || updatedRow.role.id) : null;
        const newRoleName = updates.role.label || updates.role.id;

        // If role is actually changing (not just setting a role when there was none)
        if (oldRoleName && oldRoleName !== newRoleName) {
          // For each day field, update the actual hours
          dayFields.forEach(({ field, dayNumber }) => {
            const dayHours = parseFloat((updatedRow as any)[field] as string) || 0;
            
            if (dayHours > 0 && appData?.selectedLocation?.id && createSchedulePeriod && createScheduleWeek) {
              // Remove hours from old role
              const oldRoleCumulativeHours = calculateCumulativeActualHoursForRoleAndDay(
                actualEmployeeRows,
                oldRoleName,
                field,
                id,
                0 // Set to 0 to remove from old role
              );

              dispatch(updateActualHours({
                role: oldRoleName,
                dayNumber: dayNumber,
                locationId: appData.selectedLocation.id,
                year: new Date().getFullYear(),
                period: createSchedulePeriod,
                week: createScheduleWeek,
                newActualHours: oldRoleCumulativeHours
              }));

              // Add hours to new role
              const newRoleCumulativeHours = calculateCumulativeActualHoursForRoleAndDay(
                actualEmployeeRows.map(row => row.id === id ? { ...row, role: updates.role } : row),
                newRoleName,
                field,
                id,
                dayHours
              );

              dispatch(updateActualHours({
                role: newRoleName,
                dayNumber: dayNumber,
                locationId: appData.selectedLocation.id,
                year: new Date().getFullYear(),
                period: createSchedulePeriod,
                week: createScheduleWeek,
                newActualHours: newRoleCumulativeHours
              }));
            }
          });
        } else if (!oldRoleName && newRoleName) {
          // Role is being set for the first time, add hours to new role
          dayFields.forEach(({ field, dayNumber }) => {
            const dayHours = parseFloat((updatedRow as any)[field] as string) || 0;
            
            if (dayHours > 0 && appData?.selectedLocation?.id && createSchedulePeriod && createScheduleWeek) {
              const newRoleCumulativeHours = calculateCumulativeActualHoursForRoleAndDay(
                actualEmployeeRows.map(row => row.id === id ? { ...row, role: updates.role } : row),
                newRoleName,
                field,
                id,
                dayHours
              );

              dispatch(updateActualHours({
                role: newRoleName,
                dayNumber: dayNumber,
                locationId: appData.selectedLocation.id,
                year: new Date().getFullYear(),
                period: createSchedulePeriod,
                week: createScheduleWeek,
                newActualHours: newRoleCumulativeHours
              }));
            }
          });
        }
      }

      // Check if any day field is being updated and if the employee has a role assigned
      if (updatedRow.role || updates.role) {
        const roleName = (updates.role ? updates.role.label || updates.role.id : updatedRow.role?.label || updatedRow.role?.id);
        
        // Check each day field that was updated
        dayFields.forEach(({ field, dayNumber }) => {
          if (updates.hasOwnProperty(field) && !updates.hasOwnProperty('role')) {
            const newHoursValue = parseFloat(updates[field]) || 0;
            
            // Calculate cumulative actual hours for this role and day
            const cumulativeHours = calculateCumulativeActualHoursForRoleAndDay(
              actualEmployeeRows,
              roleName,
              field,
              id,
              newHoursValue
            );

            // Update actual hours data in Redux
            if (appData?.selectedLocation?.id && createSchedulePeriod && createScheduleWeek) {
              dispatch(updateActualHours({
                role: roleName,
                dayNumber: dayNumber,
                locationId: appData.selectedLocation.id,
                year: new Date().getFullYear(),
                period: createSchedulePeriod,
                week: createScheduleWeek,
                newActualHours: cumulativeHours
              }));
            }
          }
        });
      }
    }

    // Handle child row parent total calculation (existing logic)
    if (updatedRow?.isChildRow && updatedRow.parentId) {
      const parentRow = actualEmployeeRows.find((row: ActualHoursEmployeeRowData) => row.id === updatedRow.parentId);
      if (parentRow) {
        const childRowWithUpdates = { ...updatedRow, ...updates };

        const parentHours = [
          parseFloat(parentRow.sunday) || 0,
          parseFloat(parentRow.monday) || 0,
          parseFloat(parentRow.tuesday) || 0,
          parseFloat(parentRow.wednesday) || 0,
          parseFloat(parentRow.thursday) || 0,
          parseFloat(parentRow.friday) || 0,
          parseFloat(parentRow.saturday) || 0,
        ].reduce((sum, hours) => sum + hours, 0);

        const childHours = [
          parseFloat(childRowWithUpdates.sunday) || 0,
          parseFloat(childRowWithUpdates.monday) || 0,
          parseFloat(childRowWithUpdates.tuesday) || 0,
          parseFloat(childRowWithUpdates.wednesday) || 0,
          parseFloat(childRowWithUpdates.thursday) || 0,
          parseFloat(childRowWithUpdates.friday) || 0,
          parseFloat(childRowWithUpdates.saturday) || 0,
        ].reduce((sum, hours) => sum + hours, 0);

        const newParentTotal = parentHours + childHours;

        dispatch(updateActualEmployeeRow({
          id: parentRow.id,
          updates: { total: newParentTotal.toString() }
        }));
      }
    }
  };

  // Helper function to calculate cumulative actual hours for a role and day
  const calculateCumulativeActualHoursForRoleAndDay = (
    allActualEmployeeRows: ActualHoursEmployeeRowData[],
    roleName: string,
    dayField: string,
    updatedRowId: string,
    newHoursValue: number
  ): number => {
    let cumulativeHours = 0;

    allActualEmployeeRows.forEach((row) => {
      // Only include rows with the same role
      const rowRoleName = row.role?.label || row.role?.id;
      if (rowRoleName === roleName) {
        if (row.id === updatedRowId) {
          // Use the new value for the row being updated
          cumulativeHours += newHoursValue;
        } else {
          // Use existing value for other rows
          const existingValue = parseFloat((row as any)[dayField]) || 0;
          cumulativeHours += existingValue;
        }
      }
    });

    return cumulativeHours;
  };

  const handleEmployeeRowDelete = (id: string) => {
    console.log('Employee row delete:', id);
    dispatch(removeEmployeeRow(id));
  };

  const handleActualEmployeeRowDelete = (id: string) => {
    console.log('Actual employee row delete:', id);
    dispatch(removeActualEmployeeRow(id));
  };

  // Split role handlers
  const handleSplitRole = () => {
    console.log('Split Role pressed from DLOCreateSchedule');

    const selectedEmployees = employeeRows.filter((row: EmployeeRowData) => row.isSelected);

    if (selectedEmployees.length === 1) {
      const selectedEmployee = selectedEmployees[0];

      if (!selectedEmployee.isChildRow) {
        if (!selectedEmployee.isSplit) {
          dispatch(splitEmployeeRole(selectedEmployee.id));
        } else {
          const childRows = employeeRows.filter((row: EmployeeRowData) => row.parentId === selectedEmployee.id);
          if (childRows.length > 0) {
            const lastChild = childRows[childRows.length - 1];
            dispatch(splitRoleAfterSibling({
              parentId: selectedEmployee.id,
              afterSiblingId: lastChild.id
            }));
          } else {
            dispatch(splitEmployeeRole(selectedEmployee.id));
          }
        }
      } else {
        if (selectedEmployee.parentId && !hasChildSplitRoles(selectedEmployee.id, employeeRows)) {
          dispatch(splitRoleAfterSibling({
            parentId: selectedEmployee.parentId,
            afterSiblingId: selectedEmployee.id
          }));
        }
      }
    } else if (selectedEmployees.length > 1) {
      const parentRows = selectedEmployees.filter((row: EmployeeRowData) => !row.isChildRow);
      const childRows = selectedEmployees.filter((row: EmployeeRowData) => row.isChildRow);

      if (parentRows.length === 1 && childRows.length > 0) {
        const parentEmployee = parentRows[0];
        const allChildRows = employeeRows.filter((row: EmployeeRowData) => row.parentId === parentEmployee.id);
        if (allChildRows.length > 0) {
          const lastChild = allChildRows[allChildRows.length - 1];
          dispatch(splitRoleAfterSibling({
            parentId: parentEmployee.id,
            afterSiblingId: lastChild.id
          }));
        } else {
          dispatch(splitEmployeeRole(parentEmployee.id));
        }
      }
    }
  };

  const handleActualSplitRole = () => {
    console.log('Actual Split Role pressed from DLOCreateSchedule');

    const selectedEmployees = actualEmployeeRows.filter((row: ActualHoursEmployeeRowData) => row.isSelected);

    if (selectedEmployees.length === 1) {
      const selectedEmployee = selectedEmployees[0];

      if (!selectedEmployee.isChildRow) {
        console.log('Splitting actual employee role for parent:', selectedEmployee.id);
        dispatch(splitActualEmployeeRole(selectedEmployee.id));
      } else {
        const parentId = selectedEmployee.parentId;
        if (parentId) {
          console.log('Creating actual sibling split role after:', selectedEmployee.id, 'for parent:', parentId);
          dispatch(splitActualRoleAfterSibling({ parentId, afterSiblingId: selectedEmployee.id }));
        }
      }
    } else if (selectedEmployees.length > 1) {
      const parentRows = selectedEmployees.filter((row: ActualHoursEmployeeRowData) => !row.isChildRow);
      const childRows = selectedEmployees.filter((row: ActualHoursEmployeeRowData) => row.isChildRow);

      if (parentRows.length === 1 && childRows.length > 0) {
        const parentId = parentRows[0].id;
        const lastSelectedChild = childRows[childRows.length - 1];

        console.log('Creating actual split role for parent + children scenario, parent:', parentId, 'after child:', lastSelectedChild.id);
        dispatch(splitActualRoleAfterSibling({ parentId, afterSiblingId: lastSelectedChild.id }));
      }
    }
  };

  const handleSplitRoleDelete = (childRowId: string) => {
    console.log('Delete split role pressed for:', childRowId);
    dispatch(removeSplitRole(childRowId));
  };

  const handleActualSplitRoleDelete = (childRowId: string) => {
    console.log('Delete actual split role pressed for:', childRowId);
    dispatch(removeActualSplitRole(childRowId));
  };

  const handleSelectAllEmployees = () => {
    const allSelected = employeeRows.length > 0 && employeeRows.every((row: EmployeeRowData) => row.isSelected);
    const newSelectionState = !allSelected;

    console.log(`${newSelectionState ? 'Selecting' : 'Deselecting'} all ${employeeRows.length} employees`);

    employeeRows.forEach((employeeRow: EmployeeRowData) => {
      handleEmployeeRowUpdate(employeeRow.id, { isSelected: newSelectionState });
    });
  };

  const handleSelectAllActualEmployees = () => {
    const allSelected = actualEmployeeRows.length > 0 && actualEmployeeRows.every((row: ActualHoursEmployeeRowData) => row.isSelected);
    const newSelectionState = !allSelected;

    console.log(`${newSelectionState ? 'Selecting' : 'Deselecting'} all ${actualEmployeeRows.length} actual employees`);

    actualEmployeeRows.forEach((employeeRow: ActualHoursEmployeeRowData) => {
      handleActualEmployeeRowUpdate(employeeRow.id, { isSelected: newSelectionState });
    });
  };

  const handleDeleteSelectedRows = () => {
    if (tabState.activeTab === 'actual') {
      handleDeleteSelectedActualRows();
      return;
    }

    const selectedRows = employeeRows
      .filter((row: EmployeeRowData) => row.isSelected)
      .sort((a: EmployeeRowData, b: EmployeeRowData) => Number(!!b.isChildRow) - Number(!!a.isChildRow));

    if (selectedRows.length === 0) {
      return;
    }

    selectedRows.forEach((row: EmployeeRowData) => {
      dispatch(removeEmployeeRow(row.id));
    });

    dispatch(clearSelectedDays());
  };

  const handleDeleteSelectedActualRows = () => {
    const selectedRows = actualEmployeeRows
      .filter((row: ActualHoursEmployeeRowData) => row.isSelected)
      .sort((a: ActualHoursEmployeeRowData, b: ActualHoursEmployeeRowData) => Number(!!b.isChildRow) - Number(!!a.isChildRow));

    if (selectedRows.length === 0) {
      return;
    }

    selectedRows.forEach((row: ActualHoursEmployeeRowData) => {
      dispatch(removeActualEmployeeRow(row.id));
    });

    dispatch(clearActualSelectedDays());
  };

  // Mass update handlers
  const handleMassUpdate = (hours: string) => {
    if (tabState.activeTab === 'actual') {
      handleActualMassUpdate(hours);
    } else {
      console.log('Mass Update pressed with hours:', hours);

      const selectedEmployees = employeeRows.filter((row: EmployeeRowData) => row.isSelected);

      if (selectedEmployees.length === 0) {
        console.log('No employees selected for mass update');
        return;
      }

      if (selectedDays.length === 0) {
        console.log('No days selected for mass update');
        return;
      }

      console.log(`Updating ${selectedEmployees.length} employees for days:`, selectedDays);
      console.log('Selected day keys:', selectedDays);

      selectedEmployees.forEach((employee: EmployeeRowData) => {
        const updates: any = {};

        selectedDays.forEach((day: string) => {
          switch (day.toLowerCase()) {
            case 'sun':
              updates.sunday = hours;
              break;
            case 'mon':
              updates.monday = hours;
              break;
            case 'tue':
              updates.tuesday = hours;
              break;
            case 'wed':
              updates.wednesday = hours;
              break;
            case 'thu':
              updates.thursday = hours;
              break;
            case 'fri':
              updates.friday = hours;
              break;
            case 'sat':
              updates.saturday = hours;
              break;
          }
        });

        const newTotalHours = [
          updates.sunday !== undefined ? parseFloat(updates.sunday) || 0 : parseFloat(employee.sunday) || 0,
          updates.monday !== undefined ? parseFloat(updates.monday) || 0 : parseFloat(employee.monday) || 0,
          updates.tuesday !== undefined ? parseFloat(updates.tuesday) || 0 : parseFloat(employee.tuesday) || 0,
          updates.wednesday !== undefined ? parseFloat(updates.wednesday) || 0 : parseFloat(employee.wednesday) || 0,
          updates.thursday !== undefined ? parseFloat(updates.thursday) || 0 : parseFloat(employee.thursday) || 0,
          updates.friday !== undefined ? parseFloat(updates.friday) || 0 : parseFloat(employee.friday) || 0,
          updates.saturday !== undefined ? parseFloat(updates.saturday) || 0 : parseFloat(employee.saturday) || 0,
        ].reduce((sum, dayHours) => sum + dayHours, 0);

        updates.total = newTotalHours.toString();

        console.log(`Updating employee ${employee.id} with:`, updates);
        handleEmployeeRowUpdate(employee.id, updates);
      });

      console.log(`Mass update completed: ${hours} hours applied to ${selectedDays.join(', ')} for ${selectedEmployees.length} employees`);
    }
  };

  const handleActualMassUpdate = (hours: string) => {
    console.log('Actual Mass Update pressed with hours:', hours);

    const selectedEmployees = actualEmployeeRows.filter((row: ActualHoursEmployeeRowData) => row.isSelected);

    if (selectedEmployees.length === 0) {
      console.log('No actual employees selected for mass update');
      return;
    }

    if (actualSelectedDays.length === 0) {
      console.log('No days selected for actual mass update');
      return;
    }

    console.log(`Updating ${selectedEmployees.length} actual employees for days:`, actualSelectedDays);
    console.log('Selected day keys:', actualSelectedDays);

    selectedEmployees.forEach((employee: ActualHoursEmployeeRowData) => {
      const updates: any = {};

      actualSelectedDays.forEach((day: string) => {
        switch (day.toLowerCase()) {
          case 'sun':
            updates.sunday = hours;
            break;
          case 'mon':
            updates.monday = hours;
            break;
          case 'tue':
            updates.tuesday = hours;
            break;
          case 'wed':
            updates.wednesday = hours;
            break;
          case 'thu':
            updates.thursday = hours;
            break;
          case 'fri':
            updates.friday = hours;
            break;
          case 'sat':
            updates.saturday = hours;
            break;
        }
      });

      const newTotalHours = [
        updates.sunday !== undefined ? parseFloat(updates.sunday) || 0 : parseFloat(employee.sunday) || 0,
        updates.monday !== undefined ? parseFloat(updates.monday) || 0 : parseFloat(employee.monday) || 0,
        updates.tuesday !== undefined ? parseFloat(updates.tuesday) || 0 : parseFloat(employee.tuesday) || 0,
        updates.wednesday !== undefined ? parseFloat(updates.wednesday) || 0 : parseFloat(employee.wednesday) || 0,
        updates.thursday !== undefined ? parseFloat(updates.thursday) || 0 : parseFloat(employee.thursday) || 0,
        updates.friday !== undefined ? parseFloat(updates.friday) || 0 : parseFloat(employee.friday) || 0,
        updates.saturday !== undefined ? parseFloat(updates.saturday) || 0 : parseFloat(employee.saturday) || 0,
      ].reduce((sum, dayHours) => sum + dayHours, 0);

      updates.total = newTotalHours.toString();

      console.log(`Updating actual employee ${employee.id} with:`, updates);
      handleActualEmployeeRowUpdate(employee.id, updates);
    });

    console.log(`Actual mass update completed: ${hours} hours applied to ${actualSelectedDays.join(', ')} for ${selectedEmployees.length} employees`);
  };

  // Day filter handlers
  const handleDayFilterPress = (day: string) => {
    dispatch(toggleSelectedDay(day));
  };

  const handleActualDayFilterPress = (day: string) => {
    dispatch(toggleActualSelectedDay(day));
  };

  // Submit handler
  const handleSubmit = async () => {
    // Determine if target period/week is in the future compared to current period/week
    const isFutureSchedule = targetWeek && currentWeek ? 
      compareWeek(targetWeek, currentWeek) > 0 : false;
    
    // Use schedule hours data for future schedules, actual hours data for current/past schedules
    const employeeDataForValidation = isFutureSchedule ? employeeRows : actualEmployeeRows;
    
    console.log('Validation data source:', isFutureSchedule ? 'Schedule Hours (Future)' : 'Actual Hours (Current/Past)');
    console.log('Target week:', targetWeek, 'Current week:', currentWeek, 'Is future:', isFutureSchedule);
    
    const hasEmployeesOver40Hours = validateEmployeeHoursOver40(employeeDataForValidation);
    const hasUnassignedRoles = validateUnassignedRoles(tableData, employeeDataForValidation);
    const hasForecastVariance = validateForecastVariance(tableData, employeeDataForValidation);

    // Get detailed validation information
    const overHoursDetails = getEmployeesOver40Hours(employeeDataForValidation);
    const unassignedRoleDetails = getUnassignedRoleDetails(tableData, employeeDataForValidation);
    const forecastVarianceDetails = getForecastVarianceDetails(tableData, employeeDataForValidation);

    // Validation priority:
    // 1. Unassigned roles (Error - cannot submit)
    // 2. Over 40 hours or forecast variance (Warning - can submit with confirmation)
    // 3. No issues (Confirmation)
    
    if (hasUnassignedRoles) {
      console.log('Setting submit error modal with details:', unassignedRoleDetails);
      setModalState.setSubmitErrorModal(
        true,
        'You can\'t submit: some employees have no roles assigned',
        unassignedRoleDetails
      );
    } else if (hasEmployeesOver40Hours && hasForecastVariance) {
      // Both issues exist - prioritize showing the over 40 hours warning
      setModalState.setSubmitWarningModal(
        true,
        'One or more employees are scheduled for more than 40 hours. Do you want to proceed with submission?',
        overHoursDetails
      );
    } else if (hasEmployeesOver40Hours) {
      setModalState.setSubmitWarningModal(
        true,
        'One or more employees are scheduled for more than 40 hours. Do you want to proceed with submission?',
        overHoursDetails
      );
    } else if (hasForecastVariance) {
      setModalState.setSubmitWarningModal(
        true,
        'Some roles have scheduled hours that differ from forecasted hours. Do you want to proceed with submission?',
        forecastVarianceDetails
      );
    } else {
      setModalState.setSubmitConfirmationModal(
        true,
        'Are you sure you want to submit this schedule?'
      );
    }
  };

  // Separated submission logic for reuse in modal confirmations
  const performSubmission = async () => {
    try {
      const calculatedWeekNumber = (createSchedulePeriod - 1) * 4 + createScheduleWeek;
      
      const submissionData = {
        period: createSchedulePeriod,
        week: createScheduleWeek,
        year: createScheduleYear,
        locationName: appData?.selectedLocation?.displayText || '',
        status: 'CLOSED'
      };

      const result = await dispatch(submitScheduleStatus(submissionData));

      if (submitScheduleStatus.fulfilled.match(result)) {
        showNotification('Schedule has been submitted successfully!', 'success');

        // Release all active locks after successful submission
        const lockService = getSessionLockService();
        if (lockService) {
          const serviceLocks = lockService.getActiveLocks();
          if (serviceLocks.size > 0) {
            console.log('[DLOCreateSchedule] Releasing locks after submission:', Array.from(serviceLocks.keys()));
            for (const [resourceId, lockData] of serviceLocks.entries()) {
              try {
                await lockService.releaseLock(resourceId, lockData.lockToken);
                console.log('[DLOCreateSchedule] Lock released:', resourceId);
              } catch (error) {
                console.error('[DLOCreateSchedule] Error releasing lock:', error);
              }
            }
          }
        }
        
        setTimeout(() => {
          if (onNavigate) {
            onNavigate('DLODashboard');
          }
        }, 2000);
      } else {
        const errorMessage = typeof result.payload === 'string' 
          ? result.payload 
          : (result.payload as any)?.message || 'Failed to submit schedule. Please try again.';
        
        showNotification(errorMessage, 'danger');
      }
    } catch (error) {
      console.error('Unexpected error during submission:', error);
      showNotification('An unexpected error occurred. Please try again.', 'danger');
    }
  };

  // Helper function to validate employee rows have role and shift
  const validateEmployeeRowsRoleAndShift = (rows: EmployeeRowData[] | ActualHoursEmployeeRowData[]) => {
    const invalidRows: string[] = [];
    
    rows.forEach((row, index) => {
      const rowNumber = index + 1;
      const employeeName = row.employee?.label || `Row ${rowNumber}`;
      
      // Check if role is missing or null/empty
      const hasValidRole = row.role && (row.role.id || row.role.value || row.role.label);
      
      // Check if shift is missing or null/empty
      const hasValidShift = row.shift && (row.shift.id || row.shift.value || row.shift.label);
      
      if (!hasValidRole && !hasValidShift) {
        invalidRows.push(`${employeeName}: Missing both Role and Shift`);
      } else if (!hasValidRole) {
        invalidRows.push(`${employeeName}: Missing Role`);
      } else if (!hasValidShift) {
        invalidRows.push(`${employeeName}: Missing Shift`);
      }
    });
    
    return invalidRows;
  };

  // Universal save handler
  const handleUniversalSave = async () => {
    console.log('Universal Save pressed for tab:', tabState.activeTab);

    // Validate employee rows based on active tab
    const rowsToValidate = tabState.activeTab === 'actual' ? actualEmployeeRows : employeeRows;
    const invalidRowsMsg = validateEmployeeRowsRoleAndShift(rowsToValidate);
    if (invalidRowsMsg.length > 0) {
      const errorMessage = `The following employee rows are missing required information:\n\n${invalidRowsMsg.join('\n')}`;
      console.error('Validation failed - Invalid employee rows:', invalidRowsMsg);
      setModalState.setValidationErrorModal(true, errorMessage);
      return;
    }

    const locationData = {
      locId: appData?.selectedLocation?.id || '',
      locationName: appData?.selectedLocation?.displayText || ''
    };
    const periodData = {
      period: createSchedulePeriod,
      week: createScheduleWeek,
      year: new Date().getFullYear()
    };
    const isFutureSchedule = targetWeek && currentWeek ? compareWeek(targetWeek, currentWeek) > 0 : false;

    if (tabState.activeTab === 'actual') {
      console.log('Saving actual hours data:', actualEmployeeRows);
      try {
        const transformedData = transformActualHoursToAPIFormat(actualEmployeeRows, locationData, periodData, actualDeletedEmployeeRows, scheduleEmployeeData);
        if (initialSchedules && Array.isArray(initialSchedules) && initialSchedules.length > 0) {
          const employeeOpts = getEmployeeOptions(employees || []);
          const shiftOpts = getShiftOptions(shiftTimes || {});
          const orphanedEntries = getOrphanedScheduleEntries(initialSchedules, employeeOpts, shiftOpts, locationData, periodData);
          if (orphanedEntries.length > 0) {
            const existingKeys = new Set<string>();
            transformedData.forEach(entry => {
              const shiftKey = entry.shift
                ? `${entry.shift.time_value || ''}-${entry.shift.shift_type || ''}-${entry.shift.time_format || ''}`
                : 'no-shift';
              existingKeys.add(`${entry.employeeGpid}-${entry.role}-${entry.dayNum}-${shiftKey}`);
            });
            const dedupedOrphans = orphanedEntries.filter(orphan => {
              const shiftKey = orphan.shift
                ? `${orphan.shift.time_value || ''}-${orphan.shift.shift_type || ''}-${orphan.shift.time_format || ''}`
                : 'no-shift';
              const key = `${orphan.employeeGpid}-${orphan.role}-${orphan.dayNum}-${shiftKey}`;
              if (existingKeys.has(key)) {
                console.log(`[OrphanDetection][Actual] Skipping duplicate orphan (already in payload): ${key}`);
                return false;
              }
              return true;
            });
            if (dedupedOrphans.length > 0) {
              console.log(`[OrphanDetection][Actual] Appending ${dedupedOrphans.length} orphaned entries to actual save payload (filtered ${orphanedEntries.length - dedupedOrphans.length} duplicates)`);
              transformedData.push(...dedupedOrphans);
            }
          }
        }
        if (transformedData.length === 0) {
          console.warn('No actual hours data to save');
          return;
        }
        const result = await dispatch(saveScheduleData(transformedData));
        if (saveScheduleData.fulfilled.match(result)) {
          console.log('Actual hours data saved successfully');
          dispatch(fetchCreateScheduleHours());
          dispatch(fetchScheduleEmployeeData());
          dispatch(fetchKCPHData({
            period: createSchedulePeriod,
            week: fromBackendWeek(createScheduleWeek).week,
            year: periodData.year
          }));
        } else {
          // On failure: show error, do NOT call get APIs, keep page state unchanged
          setModalState.setValidationErrorModal(true, `${result.payload || 'Unknown error'}`, 'Failed to save actual hours data');
          return;
        }
      } catch (error) {
        console.error('Error saving actual hours data:', error);
      }
    } else {
      console.log('Saving schedule data:', employeeRows);
      console.log('Is future schedule:', isFutureSchedule);
      try {
        const transformedData = transformEmployeeRowsToAPIFormat(employeeRows, locationData, periodData, deletedEmployeeRows, scheduleEmployeeData);
        if (initialSchedules && Array.isArray(initialSchedules) && initialSchedules.length > 0) {
          const employeeOpts = getEmployeeOptions(employees || []);
          const shiftOpts = getShiftOptions(shiftTimes || {});
          const orphanedEntries = getOrphanedScheduleEntries(initialSchedules, employeeOpts, shiftOpts, locationData, periodData);
          if (orphanedEntries.length > 0) {
            const existingKeys = new Set<string>();
            transformedData.forEach(entry => {
              const shiftKey = entry.shift
                ? `${entry.shift.time_value || ''}-${entry.shift.shift_type || ''}-${entry.shift.time_format || ''}`
                : 'no-shift';
              existingKeys.add(`${entry.employeeGpid}-${entry.role}-${entry.dayNum}-${shiftKey}`);
            });
            const dedupedOrphans = orphanedEntries.filter(orphan => {
              const shiftKey = orphan.shift
                ? `${orphan.shift.time_value || ''}-${orphan.shift.shift_type || ''}-${orphan.shift.time_format || ''}`
                : 'no-shift';
              const key = `${orphan.employeeGpid}-${orphan.role}-${orphan.dayNum}-${shiftKey}`;
              if (existingKeys.has(key)) {
                console.log(`[OrphanDetection] Skipping duplicate orphan (already in payload): ${key}`);
                return false;
              }
              return true;
            });
            if (dedupedOrphans.length > 0) {
              console.log(`[OrphanDetection] Appending ${dedupedOrphans.length} orphaned entries to save payload (filtered ${orphanedEntries.length - dedupedOrphans.length} duplicates)`);
              transformedData.push(...dedupedOrphans);
            }
          }
        }
        if (transformedData.length === 0) {
          console.warn('No schedule data to save');
          return;
        }
        if (isFutureSchedule) {
          console.log('Future schedule detected - copying schdHours to actualHours in payload');
          transformedData.forEach(entry => {
            entry.actualHours = entry.schdHours;
          });
        }
        const result = await dispatch(saveScheduleData(transformedData));
        if (saveScheduleData.fulfilled.match(result)) {
          console.log('Schedule data saved successfully');
          dispatch(fetchCreateScheduleHours());
          dispatch(fetchScheduleEmployeeData());
          dispatch(fetchKCPHData({
            period: createSchedulePeriod,
            week: fromBackendWeek(createScheduleWeek).week,
            year: periodData.year
          }));
        } else {
          console.log('Failed to save schedule data:', result);
          // On failure: show error, do NOT call get APIs, keep page state unchanged
          setModalState.setValidationErrorModal(true, `${result.payload || 'Unknown error'}`, 'Failed to save schedule data');
          return;
        }
      } catch (error) {
        console.error('Error saving schedule data:', error);
      }
    }
  };

  // Tab toggle handler - Allow all tab switches per truth table
  const handleTabToggle = (type: 'schedule' | 'actual') => {
    console.log('Tab toggled to:', type);
    // Close all open dropdowns when switching tabs
    onCloseAllDropdowns?.();
    setActiveTab(type);
    // Note: Auto-copying is handled by useEffect in the parent component
  };

  return {
    // Navigation handlers
    handleCreateSchedule,
    handleLocationSettings,
    handleHomePress,
    handleLaborOptimizationPress,
    handleScheduleSettings,
    
    // Filter handlers
    handleRoleFilterSave,
    handleActualRoleFilterSave,
    handleDayFilterSave,
    handleActualDayFilterSave,
    
    // Employee row handlers
    handleAddEmployeeRow,
    handleEmployeeRowUpdate,
    handleActualEmployeeRowUpdate,
    handleEmployeeRowDelete,
    handleActualEmployeeRowDelete,
    
    // Split role handlers
    handleSplitRole,
    handleActualSplitRole,
    handleSplitRoleDelete,
    handleActualSplitRoleDelete,
    handleSelectAllEmployees,
    handleSelectAllActualEmployees,
    handleDeleteSelectedRows,
    handleDeleteSelectedActualRows,
    
    // Mass update handlers
    handleMassUpdate,
    handleActualMassUpdate,
    
    // Day filter handlers
    handleDayFilterPress,
    handleActualDayFilterPress,
    
    // Submit and save handlers
    handleSubmit,
    performSubmission,
    handleUniversalSave,
    handleTabToggle,
    
    // Unsaved changes handler
    proceedWithUnsavedChanges
  };
};